// src/components/wallet/GiftCardPaymentModal.jsx
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, Loader2 } from 'lucide-react';
import apiClient from '../../api/apiClient';
import { useAuthContext } from '../../context/AuthContext';

const GiftCardPaymentModal = ({ isOpen, onClose, onSuccess, amount, selectedCard, customMessage }) => {
  const navigate = useNavigate();
  const { isLoggedIn, openAuthModal, getAuthTokens } = useAuthContext();
  const [isBuying, setIsBuying] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        setMessage('');
        setIsBuying(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBuy = async () => {
    if (!isLoggedIn) {
      openAuthModal();
      return;
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setMessage('❌ مبلغ معتبر نیست.');
      return;
    }

    setIsBuying(true);
    setMessage('');

    try {
      const token = getAuthTokens();
      if (!token) {
        throw new Error('توکن وجود ندارد');
      }

      // ✅ ارسال image_url و message به سرور
      const response = await apiClient.post('/wallet/gift-cards/buy/', {
        amount: amountNum,
        image_url: selectedCard?.url || '',
        message: customMessage?.trim() || '',
      });

      // ✅ داده برگشتی از سرور شامل image_url ذخیره شده است
      if (onSuccess) onSuccess(response.data);
      setMessage('✅ کارت هدیه با موفقیت دریافت شد.');
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      console.error(err);
      const errMsg = err.response?.data?.error || err.response?.data?.detail || 'خطا در دریافت کارت هدیه.';
      if (err.response?.status === 401) {
        setMessage('⚠️ نشست شما منقضی شده است. لطفاً دوباره وارد شوید.');
        setTimeout(() => {
          onClose();
          openAuthModal();
        }, 1500);
      } else if (err.response?.status === 400 && (errMsg.includes('موجودی') || errMsg.includes('کافی'))) {
        setMessage('⚠️ موجودی کیف پول کافی نیست.');
        setTimeout(() => {
          onClose();
          navigate('/profile/wallet');
        }, 1500);
      } else {
        setMessage(`❌ ${errMsg}`);
        setIsBuying(false);
      }
    }
  };

  return ReactDOM.createPortal(
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[9998]" onClick={onClose} />
      <div className="fixed bottom-0 left-0 right-0 z-[9999] bg-white rounded-t-2xl shadow-2xl p-5 rtl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-sm font-medium text-gray-800">پرداخت با کیف پول</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="mb-4 text-center">
          <p className="text-xs text-gray-500">مبلغ قابل پرداخت:</p>
          <p className="text-lg font-bold text-gray-800">
            {amount ? parseFloat(amount).toLocaleString() : '۰'} تومان
          </p>
        </div>

        {message && (
          <div
            className={`text-xs rounded-lg p-2 mb-3 text-center ${
              message.includes('✅')
                ? 'bg-green-50 text-green-700'
                : message.includes('⚠️')
                ? 'bg-yellow-50 text-yellow-700'
                : 'bg-red-50 text-red-700'
            }`}
          >
            {message}
          </div>
        )}

        <button
          onClick={handleBuy}
          disabled={isBuying}
          className="w-full bg-white border-2 border-purple-600 text-purple-600 py-3 rounded-lg font-medium text-sm hover:bg-purple-50 transition flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {isBuying && <Loader2 size={18} className="animate-spin" />}
          {isBuying ? 'در حال پردازش...' : 'تأیید و دریافت کارت هدیه'}
        </button>
      </div>
    </>,
    document.body
  );
};

export default GiftCardPaymentModal;